import { DoctorLayout } from "@/components/layout/doctor-layout";
import { useState, useEffect, useCallback } from "react";
import { GraduationCap, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";
import { useToast } from "@/hooks/use-toast";

type CpdCategory = "clinical" | "educational" | "professional";

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: CpdCategory;
  creditHours: number;
  reflectionNotes: string | null;
  evidenceNotes: string | null;
  createdAt: string;
}

interface CpdSummary {
  total: number;
  clinical: number;
  educational: number;
  professional: number;
  year: number;
}

const CATEGORY_LABELS: Record<CpdCategory, string> = {
  clinical: "Clinical (Category 1)",
  educational: "Educational (Category 2)",
  professional: "Professional (Category 3)",
};

const CATEGORY_COLORS: Record<CpdCategory, string> = {
  clinical: "bg-blue-50 text-blue-700 border-blue-200",
  educational: "bg-violet-50 text-violet-700 border-violet-200",
  professional: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const ANNUAL_TARGET = 50;
const currentYear = new Date().getFullYear();

function SummaryBar({ summary }: { summary: CpdSummary }) {
  const pct = Math.min((summary.total / ANNUAL_TARGET) * 100, 100);
  const colour = summary.total >= ANNUAL_TARGET ? "bg-emerald-500" : summary.total >= 30 ? "bg-indigo-500" : "bg-amber-500";

  return (
    <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 rounded-2xl p-6 text-white">
      <div className="flex items-center gap-3 mb-4">
        <GraduationCap className="h-8 w-8 text-indigo-300" />
        <div>
          <h2 className="text-xl font-bold">CPD Activity Log</h2>
          <p className="text-indigo-300 text-sm">{summary.year} · RCP Credit Categories</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-2xl font-bold">{summary.total.toFixed(1)}</p>
          <p className="text-xs text-indigo-300">Total hours</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.clinical.toFixed(1)}</p>
          <p className="text-xs text-indigo-300">Clinical (Cat 1)</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.educational.toFixed(1)}</p>
          <p className="text-xs text-indigo-300">Educational (Cat 2)</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.professional.toFixed(1)}</p>
          <p className="text-xs text-indigo-300">Professional (Cat 3)</p>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-sm text-indigo-200">
          <span>Progress to {ANNUAL_TARGET}-hour GMC annual target</span>
          <span>{summary.total.toFixed(1)} / {ANNUAL_TARGET} hrs</span>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${colour}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

interface AddFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

function AddActivityForm({ onSaved, onCancel }: AddFormProps) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [category, setCategory] = useState<CpdCategory>("clinical");
  const [creditHours, setCreditHours] = useState("");
  const [reflectionNotes, setReflectionNotes] = useState("");
  const [evidenceNotes, setEvidenceNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date || !creditHours) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/doctor/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          category,
          creditHours: parseFloat(creditHours),
          reflectionNotes: reflectionNotes.trim() || undefined,
          evidenceNotes: evidenceNotes.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSaved();
    } catch {
      setError("Failed to save activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-indigo-200 p-6 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">Add CPD Activity</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="doc-cpd-title" className="block text-xs font-medium text-slate-600 mb-1.5">Activity Title *</label>
          <input
            id="doc-cpd-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. BSR Annual Meeting 2026"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label htmlFor="doc-cpd-date" className="block text-xs font-medium text-slate-600 mb-1.5">Date *</label>
          <input
            id="doc-cpd-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label htmlFor="doc-cpd-hours" className="block text-xs font-medium text-slate-600 mb-1.5">Credit Hours *</label>
          <input
            id="doc-cpd-hours"
            type="number"
            min="0.5"
            step="0.5"
            value={creditHours}
            onChange={(e) => setCreditHours(e.target.value)}
            placeholder="e.g. 6"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="doc-cpd-category" className="block text-xs font-medium text-slate-600 mb-1.5">RCP Category *</label>
          <select
            id="doc-cpd-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as CpdCategory)}
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="clinical">Clinical (Category 1) — Clinical work, patient care, clinical audit</option>
            <option value="educational">Educational (Category 2) — Teaching, training, appraisal, supervision</option>
            <option value="professional">Professional (Category 3) — Management, leadership, research, publication</option>
          </select>
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="doc-cpd-reflection" className="block text-xs font-medium text-slate-600">Reflection Notes</label>
            <VoiceInputButton
              tone="indigo"
              size="xs"
              onTranscript={(chunk) => setReflectionNotes((prev) => appendVoiceChunk(prev, chunk))}
              label="Dictate reflection"
            />
          </div>
          <textarea
            id="doc-cpd-reflection"
            value={reflectionNotes}
            onChange={(e) => setReflectionNotes(e.target.value)}
            rows={3}
            placeholder="What did you learn? How will this improve your practice?"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="doc-cpd-evidence" className="block text-xs font-medium text-slate-600 mb-1.5">Evidence Notes</label>
          <input
            id="doc-cpd-evidence"
            value={evidenceNotes}
            onChange={(e) => setEvidenceNotes(e.target.value)}
            placeholder="e.g. Certificate reference, programme number"
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-indigo-700 hover:bg-indigo-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {saving ? "Saving..." : "Save Activity"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function DoctorCpdLog() {
  const { toast } = useToast();
  const [year, setYear] = useState(currentYear);
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [summary, setSummary] = useState<CpdSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const activitiesRes = await fetch(`/api/doctor/cpd?year=${year}`);
      if (activitiesRes.ok) {
        const data: CpdActivity[] = await activitiesRes.json();
        setActivities(data);
        let clinical = 0, educational = 0, professional = 0;
        for (const a of data) {
          if (a.category === "clinical") clinical += a.creditHours;
          else if (a.category === "educational") educational += a.creditHours;
          else professional += a.creditHours;
        }
        setSummary({ total: clinical + educational + professional, clinical, educational, professional, year });
      }
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this CPD activity? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/doctor/cpd/${id}`, { method: "DELETE" });
      setActivities((prev) => prev.filter((a) => a.id !== id));
      load();
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await fetch("/api/doctor/cpd/export.pdf");
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rheumhub-cpd-portfolio-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: "Couldn't generate PDF",
        description: "Something went wrong generating your appraisal PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <DoctorLayout title="CPD Activity Log">
      <div className="space-y-6">
        {summary && <SummaryBar summary={summary} />}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label htmlFor="doc-cpd-year" className="text-sm font-medium text-slate-600">Year:</label>
            <select
              id="doc-cpd-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleExportPdf}
              disabled={exporting}
              variant="outline"
              className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Generating…" : "Export for Appraisal"}
            </Button>
            <Button
              onClick={() => { setShowForm(true); }}
              className="bg-indigo-700 hover:bg-indigo-800 text-white gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Activity
            </Button>
          </div>
        </div>

        {showForm && (
          <AddActivityForm
            onSaved={() => { setShowForm(false); load(); }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-2xl" />)}
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <GraduationCap className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No CPD activities logged for {year}</p>
            <p className="text-sm mt-1">Add your first activity to start tracking your professional development.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <div key={a.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedId(expanded ? null : a.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(expanded ? null : a.id); }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[a.category]}`}>
                          {CATEGORY_LABELS[a.category]}
                        </span>
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{a.title}</p>
                      <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-indigo-700">{a.creditHours} hrs</p>
                    </div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </div>
                  {expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                      {a.reflectionNotes && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">Reflection</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{a.reflectionNotes}</p>
                        </div>
                      )}
                      {a.evidenceNotes && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">Evidence</p>
                          <p className="text-sm text-slate-700">{a.evidenceNotes}</p>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDelete(a.id)}
                          disabled={deletingId === a.id}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          {deletingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCP Categories:</strong> Category 1 (Clinical) covers direct patient care, clinical audit, and quality improvement. Category 2 (Educational) covers teaching, training, and supervision. Category 3 (Professional) covers management, research, and leadership. The GMC recommends a minimum of 50 CPD credits per year with a balance across categories. CPD credits recorded here are for your personal record only and do not constitute formal GMC validation.
        </div>
      </div>
    </DoctorLayout>
  );
}
